import * as XLSX from 'xlsx'

/**
 * Excel Export Utilities for Curve Features Statistics
 * Provides functions for downloading curve features statistics as Excel files
 */

/**
 * Checks if a column is flag-related
 * @param {string} column - Column name
 * @returns {boolean} - True if column is flag-related
 */
function isFlagColumn(column) {
  // Flag columns start with FLAG_ or are validation/override columns
  const flagPatterns = [
    /^FLAG_/i,
    /^CURVE_VALID$/i,
    /^PERIPHERY_VALID$/i,
    /^override_flags$/i,
    /^override_note$/i
  ]
  
  return flagPatterns.some(pattern => pattern.test(column))
}

/**
 * Orders columns by grouping them with suffixes: _CURVE, _REGION, _PERIPHERY_BEFORE, _PERIPHERY_AFTER
 * Columns without these suffixes come first, then grouped by suffix
 * Flag columns are placed at the end
 * @param {Array} columns - Array of column names
 * @returns {Array} - Ordered array of column names
 */
function orderColumnsBySuffix(columns) {
  // Define suffix priority order
  const suffixOrder = ['_CURVE', '_REGION', '_PERIPHERY_BEFORE', '_PERIPHERY_AFTER']
  
  // Separate columns by suffix and flags
  const columnsBySuffix = {
    noSuffix: [],
    _CURVE: [],
    _REGION: [],
    _PERIPHERY_BEFORE: [],
    _PERIPHERY_AFTER: [],
    flags: []
  }
  
  columns.forEach(col => {
    // Check if it's a flag column first
    if (isFlagColumn(col)) {
      columnsBySuffix.flags.push(col)
      return
    }
    
    let assigned = false
    // Check each suffix in order (longest first to avoid partial matches)
    for (const suffix of suffixOrder.sort((a, b) => b.length - a.length)) {
      if (col.endsWith(suffix)) {
        columnsBySuffix[suffix].push(col)
        assigned = true
        break
      }
    }
    if (!assigned) {
      columnsBySuffix.noSuffix.push(col)
    }
  })
  
  // Sort columns within each group alphabetically
  Object.keys(columnsBySuffix).forEach(key => {
    columnsBySuffix[key].sort()
  })
  
  // Build final ordered array: no suffix first, then by suffix priority, then flags at the end
  const orderedColumns = [
    ...columnsBySuffix.noSuffix,
    ...columnsBySuffix._CURVE,
    ...columnsBySuffix._REGION,
    ...columnsBySuffix._PERIPHERY_BEFORE,
    ...columnsBySuffix._PERIPHERY_AFTER,
    ...columnsBySuffix.flags
  ]
  
  return orderedColumns
}

/**
 * Downloads curve features statistics as an Excel file
 * @param {Object} params - Export parameters
 * @param {Array} params.curveMetrics - Array of metric definitions with key and label
 * @param {Object} params.curveStats - Statistics object with total, valid, invalid stats
 * @param {Object} params.subjectInfo - Subject information (subid, dataset_identifier)
 * @param {number} params.totalCurves - Total number of curves
 * @param {number} params.validCount - Number of valid curves
 * @param {number} params.invalidCount - Number of invalid curves
 * @param {Array} params.curveFeatures - Optional: Full curve features data array
 * @returns {Promise} - Download promise
 */
export async function downloadCurveFeaturesStatistics({ 
  curveMetrics = [],
  curveStats = {},
  subjectInfo = {},
  totalCurves = 0,
  validCount = 0,
  invalidCount = 0,
  curveFeatures = []
}) {
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new()

    // Sheet 1: Feature Statistics
    const statsDataArray = createFeatureStatsSheet(curveMetrics, curveStats, subjectInfo, totalCurves, validCount, invalidCount)
    const statsSheet = XLSX.utils.aoa_to_sheet(statsDataArray)
    XLSX.utils.book_append_sheet(workbook, statsSheet, 'Feature Statistics')

    // Sheet 2: Full Curve Features Data (if provided)
    if (curveFeatures && curveFeatures.length > 0) {
      // Get all unique column names from all curves
      const allColumns = new Set()
      curveFeatures.forEach(curve => {
        Object.keys(curve).forEach(col => allColumns.add(col))
      })
      
      // Order columns by suffix
      const orderedColumns = orderColumnsBySuffix(Array.from(allColumns))
      
      // Create data array with ordered columns
      const dataArray = [orderedColumns] // Header row
      curveFeatures.forEach(curve => {
        const row = orderedColumns.map(col => curve[col] ?? '')
        dataArray.push(row)
      })
      
      const featuresSheet = XLSX.utils.aoa_to_sheet(dataArray)
      XLSX.utils.book_append_sheet(workbook, featuresSheet, 'Curve Features')
    }

    // Generate filename
    const subid = subjectInfo.subid || 'Unknown'
    const datasetId = subjectInfo.dataset_identifier || '001'
    const timestamp = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
    const filename = `${subid}_${datasetId}_curve_features_statistics_${timestamp}.xlsx`

    // Write and download the file
    XLSX.writeFile(workbook, filename)

    return { success: true, filename }
  } catch (error) {
    console.error('Curve features statistics export failed:', error)
    throw error
  }
}

/**
 * Creates the feature statistics data sheet
 * @param {Array} curveMetrics - Array of metric definitions
 * @param {Object} curveStats - Statistics object with total, valid, invalid
 * @param {Object} subjectInfo - Subject information
 * @param {number} totalCurves - Total number of curves
 * @param {number} validCount - Number of valid curves
 * @param {number} invalidCount - Number of invalid curves
 * @returns {Array} - Worksheet data array
 */
function createFeatureStatsSheet(curveMetrics, curveStats, subjectInfo, totalCurves, validCount, invalidCount) {
  const data = [
    ['Curve Features Statistics Report'],
    [''],
    ['Subject Information:'],
    ['Subject ID', subjectInfo.subid || 'N/A'],
    ['Dataset', subjectInfo.dataset_identifier || 'N/A'],
    ['Total Curves', totalCurves],
    ['High-Quality Curves', validCount],
    ['Low-Quality Curves', invalidCount],
    [''],
    ['Feature Statistics:'],
    ['Metric', 'Category', 'Mean', 'SD', 'Min', 'Max', 'Count']
  ]

  // Add statistics for each metric
  curveMetrics.forEach(metric => {
    const metricLabel = metric.label || metric.key

    // High-Quality statistics
    const validStatsObj = curveStats.valid?.[metric.key]
    const validStats = parseStatsString(validStatsObj)
    if (validStats) {
      data.push([
        metricLabel,
        'High-Quality',
        validStats.mean !== null ? validStats.mean.toFixed(1) : '–',
        validStats.sd !== null ? validStats.sd.toFixed(1) : '–',
        validStats.min !== null ? validStats.min.toFixed(1) : '–',
        validStats.max !== null ? validStats.max.toFixed(1) : '–',
        validStats.count || 0
      ])
    } else {
      data.push([
        metricLabel,
        'High-Quality',
        '–',
        '–',
        '–',
        '–',
        0
      ])
    }

    // Low-Quality statistics
    const invalidStatsObj = curveStats.invalid?.[metric.key]
    const invalidStats = parseStatsString(invalidStatsObj)
    if (invalidStats) {
      data.push([
        metricLabel,
        'Low-Quality',
        invalidStats.mean !== null ? invalidStats.mean.toFixed(1) : '–',
        invalidStats.sd !== null ? invalidStats.sd.toFixed(1) : '–',
        invalidStats.min !== null ? invalidStats.min.toFixed(1) : '–',
        invalidStats.max !== null ? invalidStats.max.toFixed(1) : '–',
        invalidStats.count || 0
      ])
    } else {
      data.push([
        metricLabel,
        'Low-Quality',
        '–',
        '–',
        '–',
        '–',
        0
      ])
    }

    // Total statistics
    const totalStatsObj = curveStats.total?.[metric.key]
    const totalStats = parseStatsString(totalStatsObj)
    if (totalStats) {
      data.push([
        metricLabel,
        'Total',
        totalStats.mean !== null ? totalStats.mean.toFixed(1) : '–',
        totalStats.sd !== null ? totalStats.sd.toFixed(1) : '–',
        totalStats.min !== null ? totalStats.min.toFixed(1) : '–',
        totalStats.max !== null ? totalStats.max.toFixed(1) : '–',
        totalStats.count || 0
      ])
    } else {
      data.push([
        metricLabel,
        'Total',
        '–',
        '–',
        '–',
        '–',
        0
      ])
    }

    // Add empty row between metrics for readability
    data.push([''])
  })

  // Add footer
  data.push([''])
  data.push(['Report Generated:', new Date().toLocaleString()])

  return data
}

/**
 * Parses a stats string like "121.8 [14.2, 398.1]" or extracts from stats object
 * @param {string|Object} statsInput - Stats string or object
 * @returns {Object} - Parsed stats object with mean, min, max, sd, count
 */
function parseStatsString(statsInput) {
  if (!statsInput) return null

  // If it's already an object with the stats, use it directly
  if (typeof statsInput === 'object' && statsInput !== null) {
    return {
      mean: statsInput.mean !== undefined ? Number(statsInput.mean) : null,
      sd: statsInput.sd !== undefined ? Number(statsInput.sd) : null,
      min: statsInput.min !== undefined ? Number(statsInput.min) : null,
      max: statsInput.max !== undefined ? Number(statsInput.max) : null,
      count: statsInput.count !== undefined ? Number(statsInput.count) : 0
    }
  }

  // Otherwise, parse the string format "mean [min, max]"
  if (typeof statsInput === 'string') {
    const match = statsInput.match(/^([\d.]+)\s*\[([\d.]+),\s*([\d.]+)\]$/)
    if (match) {
      return {
        mean: Number(match[1]),
        sd: null, // SD not in string format
        min: Number(match[2]),
        max: Number(match[3]),
        count: null // Count not in string format
      }
    }
  }

  return null
}

/**
 * Validates that required data is available for export
 * @param {Object} params - Export parameters
 * @returns {boolean} - True if data is valid for export
 */
export function validateCurveFeaturesData(params) {
  const { curveMetrics = [], curveStats = {}, subjectInfo = {} } = params
  
  if (!curveMetrics || curveMetrics.length === 0) {
    console.warn('No curve metrics available for export')
    return false
  }
  
  if (!subjectInfo.subid) {
    console.warn('No subject ID available for export')
    return false
  }
  
  return true
}

